import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  entitiesQueryOptions,
  useEntities,
} from "@/lib/queries/secure-llm/getEntities";
import { llmsQueryOptions, useLlms } from "@/lib/queries/secure-llm/getLlms";
import {
  myChatsQueryOptions,
  useMyChats,
} from "@/lib/queries/secure-llm/getMyChats";
import {
  myTokensQueryOptions,
  useMyTokens,
} from "@/lib/queries/secure-llm/getMyTokens";
import {
  secureLlmLimitQueryOptions,
  useSecureLlmLimit,
} from "@/lib/queries/secure-llm/getSecureLlmLimit";
import { userPlanQueryOptions } from "@/lib/queries/payment/userPlan";
import { useSelectedEntities } from "@/lib/hooks/secure-llm/use-selected-entities";
import { useSecureLlmChat } from "@/lib/hooks/secure-llm/use-secure-llm-chat";
import { useTokenManager } from "@/lib/hooks/secure-llm/use-token-manager";
import Body from "@/components/secure-llm/Body";
import Footer from "@/components/secure-llm/Footer";
import { ChatHeader } from "@/components/secure-llm/ChatHeader";
import { Sidebar } from "@/components/secure-llm/Sidebar";
import { MobileToolbar } from "@/components/secure-llm/MobileToolbar";
import { ChatHistoryPanel } from "@/components/secure-llm/ChatHistoryPanel";
import { PreferencesPanel } from "@/components/secure-llm/PreferencesPanel";
import { UsageLimitBar } from "@/components/secure-llm/UsageLimitBar";
import {
  DeleteChatDialog,
  DeleteTokenDialog,
  SuccessDialog,
  TokenDialog,
} from "@/components/secure-llm/Dialogs";
import { SecureLlmLoading } from "@/components/secure-llm/SecureLlmLoading";

export const Route = createFileRoute("/_app/secure-llm")({
  component: RouteComponent,
  loader({ context: { queryClient } }) {
    [
      userPlanQueryOptions,
      entitiesQueryOptions,
      llmsQueryOptions,
      myChatsQueryOptions,
      myTokensQueryOptions,
      secureLlmLimitQueryOptions,
    ].forEach((q) => queryClient.prefetchQuery(q as unknown as any));
  },
  staticData: {
    title: "Secure LLM Chat",
  },
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "Keep your AI chats private with Polygraf AI's Secure LLM Chat - local or on-premises deployments, end-to-end encrypted conversations, detailed audit logs, and zero-trust governance for mission-critical workflows.",
      },
    ],
  }),
});

function RouteComponent() {
  const planQuery = useQuery(userPlanQueryOptions);

  const entitiesQuery = useEntities();
  const llmsQuery = useLlms();
  const myChatsQuery = useMyChats();
  const myTokensQuery = useMyTokens();
  const limitQuery = useSecureLlmLimit();

  const entities = entitiesQuery.data || [];
  const llms = llmsQuery.data || [];
  const chats = myChatsQuery.data?.items || [];
  const tokens = myTokensQuery.data || [];
  const usageLimit = limitQuery.data ?? { limit: 0, usage: 0 };

  const isInitialLoading =
    planQuery.isLoading ||
    entitiesQuery.isLoading ||
    llmsQuery.isLoading ||
    myChatsQuery.isLoading ||
    myTokensQuery.isLoading ||
    limitQuery.isLoading;

  const { selectedEntities, setSelectedEntities } =
    useSelectedEntities(entities);

  const chat = useSecureLlmChat({ chats, llms, selectedEntities });
  const tokenManager = useTokenManager(llms, tokens);

  if (isInitialLoading) {
    return <SecureLlmLoading />;
  }

  const isUnlimited = usageLimit.limit === -1;
  const isLimitReached =
    !isUnlimited && usageLimit.limit > 0 && usageLimit.usage >= usageLimit.limit;
  const hasSecureLlm =
    planQuery.data?.info?.plan.features.available_services.includes(
      "SECURE_LLM",
    ) ?? false;

  const historyContent = (
    <ChatHistoryPanel
      chats={chats}
      currentChatId={chat.currentChatId}
      chatHistory={chat.chatHistory}
      chatFiles={chat.chatFiles}
      selectedFiles={chat.selectedFiles}
      loading={chat.loading}
      deletingChats={chat.deletingChats}
      onSelectChat={chat.setCurrentChatId}
      onDeleteChat={chat.confirmDeleteChat}
      onDeleteFile={chat.deleteChatFile}
      onToggleFile={chat.toggleSelectedFile}
    />
  );

  const preferencesContent = (
    <PreferencesPanel
      llms={llms}
      tokens={tokens}
      entities={entities}
      selectedModel={chat.selectedModel}
      selectedEntities={selectedEntities}
      onSelectModel={chat.setSelectedModel}
      onChangeEntities={setSelectedEntities}
      onAddToken={() => tokenManager.handleOpenTokenDialog("add")}
      onEditToken={(model, tokenId) =>
        tokenManager.handleOpenTokenDialog("edit", model, tokenId)
      }
      onDeleteToken={tokenManager.handleOpenDeleteTokenDialog}
    />
  );

  const usageLimitBar = (
    <UsageLimitBar
      usage={usageLimit.usage}
      limit={usageLimit.limit}
      isLoading={limitQuery.isLoading}
      endDate={planQuery.data?.info?.end_date}
    />
  );

  return (
    <div className="flex h-full flex-col md:flex-row md:gap-4">
      <DeleteChatDialog
        open={chat.isDeleteDialogOpen}
        onOpenChange={chat.setIsDeleteDialogOpen}
        onConfirm={() => chat.chatToDelete && chat.deleteChat(chat.chatToDelete)}
      />

      <DeleteTokenDialog
        open={tokenManager.deleteTokenDialogOpen}
        onOpenChange={tokenManager.setDeleteTokenDialogOpen}
        onConfirm={() =>
          tokenManager.tokenToDelete &&
          tokenManager.handleDeleteToken(tokenManager.tokenToDelete)
        }
        isDeleting={tokenManager.deletingToken}
      />

      <TokenDialog
        open={tokenManager.tokenDialogOpen}
        onOpenChange={tokenManager.setTokenDialogOpen}
        mode={tokenManager.tokenDialogMode}
        availableLlms={tokenManager.availableLlmsForToken}
        selectedLlm={tokenManager.selectedLlmForToken}
        onSelectLlm={tokenManager.setSelectedLlmForToken}
        tokenInputValue={tokenManager.tokenInputValue}
        onTokenInputChange={tokenManager.setTokenInputValue}
        error={tokenManager.tokenDialogError}
        onSubmit={tokenManager.handleAddOrEditToken}
        isSubmitting={
          tokenManager.addTokenMutation.isPending ||
          tokenManager.editTokenMutation.isPending
        }
      />

      <SuccessDialog
        open={tokenManager.successDialogOpen}
        onOpenChange={tokenManager.setSuccessDialogOpen}
      />

      <MobileToolbar
        historyContent={historyContent}
        preferencesContent={preferencesContent}
        usageLimitBar={usageLimitBar}
        onNewChat={chat.openNewChat}
      />

      <div className="flex-1 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex h-full flex-col">
          <ChatHeader
            showNewChatButton={hasSecureLlm}
            onNewChat={chat.openNewChat}
          />
          <Body
            chatHistory={chat.chatHistory}
            chatBoxRef={chat.chatBoxRef}
            isLoading={chat.isConversationLoading}
            occuredEntities={chat.occuredEntities}
            viewMode={chat.viewMode}
          />
          <Footer
            handleTextArea={chat.handleTextArea}
            currentMessage={chat.currentMessage}
            loading={chat.loading}
            sendText={chat.sendText}
            redactText={chat.redactText}
            redactedText={chat.redactedText}
            redacted={chat.redacted}
            setRedacted={chat.setRedacted}
            changedValues={chat.changedValues}
            setChangedValues={chat.setChangedValues}
            setRedactedText={chat.setRedactedText}
            addFileToChat={chat.addFileToChat}
            setError={chat.setError}
            error={chat.error}
            plan={planQuery.data!.info!}
            viewMode={chat.viewMode}
            toggleViewMode={chat.toggleViewMode}
            isNewChat={chat.isNewChat}
            occuredEntitiesLength={chat.occuredEntities.length}
            isLimitReached={isLimitReached}
          />
        </div>
      </div>

      <Sidebar
        historyContent={historyContent}
        preferencesContent={preferencesContent}
        usageLimitBar={usageLimitBar}
      />
    </div>
  );
}
